import {
  Scene,
  WebGLRenderer,
  Color,
  Clock,
  Group,
  Fog,
  PCFSoftShadowMap,
  SRGBColorSpace,
  Vector3,
  type Object3D,
  Mesh,
  InstancedMesh,
} from 'three';
import { createCameraRig, type CameraRig, type CameraMode } from './camera';
import { installLighting, type LightingRig } from './lights';
import { applyHingeAngle, type HingeDef } from './hinges';
import { EgoController } from './ego/egoController';
import { buildCollisionOctree } from './ego/collisionOctree';

export type ThemeMode = 'dark' | 'light';

export interface ViewportOptions {
  container: HTMLElement;
  theme: ThemeMode;
  onIdle?: () => void;
  idleDelayMs?: number;
  /** Called when the user ESCs out of ego mode (browser released the
   *  pointer lock). The App uses this to flip the React `egoMode`
   *  flag back to false. */
  onEgoExit?: () => void;
}

export interface Viewport {
  scene: Scene;
  renderer: WebGLRenderer;
  camera: CameraRig;
  lights: LightingRig;
  /** The node that house builders populate. Replace its children to regenerate. */
  houseRoot: Group;
  /** The node for generator debug overlays. */
  debugRoot: Group;
  setTheme(theme: ThemeMode): void;
  setCameraMode(mode: CameraMode): void;
  setAutoRotate(on: boolean): void;
  setStoryPeel(count: number): void;
  /** 0 = sunrise, 0.5 = midday, 1 = sunset. */
  setSun(t: number): void;
  /** Toggle the visibility of context geometry (BasePlate, Fence, Mailbox)
   *  so the user can study the house body in isolation. */
  setShowContext(on: boolean): void;
  /** Request a new target swing value (0..1) for the named hinge. The
   *  rotation is animated smoothly in the render tick, not asserted
   *  instantly — so a door/window/gate visually swings between states
   *  instead of popping. When `inverted=true` the leaf swings the
   *  opposite direction at the same hinge edge (inversion itself is
   *  applied immediately — it's a semantic flip, not a motion). No-op
   *  if the hinge group is not present in the live scene. */
  setHingeAngle(id: string, t: number, inverted?: boolean): void;
  /** Discard all animated hinge state. Call before repopulating hinges
   *  after a house regeneration so stale ids don't keep ticking. */
  resetHingeAnimations(): void;
  /** Enter first-person ego mode. Spawns the player ~1.5 m behind the
   *  front gate, facing the house, and requests pointer lock. Safe to
   *  call when already in ego mode (re-spawns). */
  enterEgoMode(): void;
  /** Exit ego mode and restore the orbit camera. Safe to call when
   *  already out of ego mode. */
  exitEgoMode(): void;
  /** Rebuild the collision octree from the current house root. Called
   *  after each house regeneration and on ego-mode entry; the octree
   *  is stable across hinge changes (see collisionOctree.ts) so no
   *  per-toggle rebuild is needed. */
  rebuildCollision(): void;
  requestRedraw(): void;
  dispose(): void;
}

const THEME_COLORS: Record<ThemeMode, { background: number; fog: number }> = {
  dark: { background: 0x0d1117, fog: 0x0d1117 },
  light: { background: 0xeef0f3, fog: 0xeef0f3 },
};

/**
 * Disposes a subtree's geometries and materials. Materials that are shared via
 * a MaterialKit should be disposed through that kit instead; this helper only
 * frees per-mesh geometry plus any mesh-local materials flagged with
 * `userData.ownsMaterial === true`.
 */
export function disposeSubtree(root: Object3D): void {
  root.traverse((obj) => {
    if (obj instanceof Mesh || obj instanceof InstancedMesh) {
      obj.geometry?.dispose?.();
      if (obj.userData?.ownsMaterial) {
        const mat = obj.material;
        if (Array.isArray(mat)) mat.forEach((m) => m.dispose?.());
        else mat?.dispose?.();
      }
    }
  });
}

export function createViewport(opts: ViewportOptions): Viewport {
  const { container, theme } = opts;

  const renderer = new WebGLRenderer({ antialias: true, alpha: false, powerPreference: 'high-performance' });
  renderer.outputColorSpace = SRGBColorSpace;
  renderer.shadowMap.enabled = true;
  renderer.shadowMap.type = PCFSoftShadowMap;
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  const initialWidth = container.clientWidth || 1;
  const initialHeight = container.clientHeight || 1;
  renderer.setSize(initialWidth, initialHeight, false);
  container.appendChild(renderer.domElement);

  const scene = new Scene();
  scene.background = new Color(THEME_COLORS[theme].background);
  scene.fog = new Fog(THEME_COLORS[theme].fog, 45, 95);

  const camera = createCameraRig(renderer.domElement, initialWidth, initialHeight);
  const lights = installLighting(scene);

  const houseRoot = new Group();
  houseRoot.name = 'HouseRoot';
  scene.add(houseRoot);

  const debugRoot = new Group();
  debugRoot.name = 'DebugRoot';
  debugRoot.visible = false;
  scene.add(debugRoot);

  let autoRotate = false;
  let idleSince = performance.now();
  const idleDelayMs = opts.idleDelayMs ?? 4000;
  const clock = new Clock();

  // Ego-mode controller. We instantiate it up-front (cheap: just a
  // camera + a couple of Vector3s) so rebuildCollision can push the
  // octree in before the user ever enters the mode.
  const ego = new EgoController(renderer.domElement, scene, initialWidth / initialHeight);

  // Hinge animation state. `targets[id]` is the value the user most
  // recently asked for (via slider drag or an E-toggle); `currents[id]`
  // is what's currently applied to the scene-graph rotation. Each tick
  // we glide `currents` toward `targets` so doors / windows / gates
  // swing between states instead of popping. The inversion flag is
  // applied immediately because it's a semantic change (the hinge edge
  // swaps direction) rather than motion along the existing arc.
  const hingeTargets = new Map<string, number>();
  const hingeCurrents = new Map<string, number>();
  const hingeInverted = new Map<string, boolean>();
  // Tuned for "brisk but readable": a full closed→open swing takes
  // about 0.4 s at this rate. Fast enough that E-toggles feel snappy,
  // slow enough that the player can watch the door move.
  const HINGE_ANIM_RATE = 2.5;
  const applyHingeCurrent = (id: string): void => {
    const node = houseRoot.getObjectByName(`Hinge_${id}`);
    if (!node || !(node instanceof Group)) return;
    const def = node.userData?.hinge as HingeDef | undefined;
    if (!def) return;
    const t = hingeCurrents.get(id) ?? 0;
    const inv = hingeInverted.get(id) ?? false;
    applyHingeAngle(node, def, t, inv);
  };
  const tickHingeAnimations = (dt: number): void => {
    if (hingeTargets.size === 0) return;
    const stepMax = HINGE_ANIM_RATE * dt;
    for (const [id, target] of hingeTargets) {
      const cur = hingeCurrents.get(id);
      if (cur === undefined || cur === target) continue;
      const delta = target - cur;
      const step = Math.abs(delta) <= stepMax ? delta : Math.sign(delta) * stepMax;
      hingeCurrents.set(id, cur + step);
      applyHingeCurrent(id);
    }
  };

  const resetIdle = () => {
    idleSince = performance.now();
  };
  renderer.domElement.addEventListener('pointerdown', resetIdle);
  renderer.domElement.addEventListener('wheel', resetIdle, { passive: true });
  window.addEventListener('keydown', resetIdle);

  const prefersReducedMotion =
    typeof window.matchMedia === 'function'
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
      : false;

  let running = true;
  const tick = () => {
    if (!running) return;
    const dt = clock.getDelta();

    // Hinges tween each frame regardless of camera mode — the user can
    // drag a slider in orbit view and watch the door swing, and an
    // E-toggle in ego mode should also animate from the orbit
    // perspective if they pop back out mid-swing.
    tickHingeAnimations(dt);

    if (ego.active) {
      // Ego mode owns the camera: run the FP controller step and
      // render from its camera. Orbit controls, idle auto-rotate and
      // the camera rig are all paused for the duration of the mode.
      ego.update(dt);
      renderer.render(scene, ego.camera);
      requestAnimationFrame(tick);
      return;
    }

    if (autoRotate && !prefersReducedMotion) {
      const idleMs = performance.now() - idleSince;
      if (idleMs > idleDelayMs) {
        const cam = camera.active();
        const t = camera.controls.target;
        const dx = cam.position.x - t.x;
        const dz = cam.position.z - t.z;
        const angle = 0.25 * dt; // ~14 deg/sec
        const cos = Math.cos(angle);
        const sin = Math.sin(angle);
        cam.position.x = t.x + dx * cos - dz * sin;
        cam.position.z = t.z + dx * sin + dz * cos;
        cam.lookAt(t);
      }
    }

    camera.controls.update();
    renderer.render(scene, camera.active());
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);

  const onResize = () => {
    const w = container.clientWidth || 1;
    const h = container.clientHeight || 1;
    renderer.setSize(w, h, false);
    camera.resize(w, h);
    ego.resize(w / h);
  };
  const ro = new ResizeObserver(onResize);
  ro.observe(container);

  const viewport: Viewport = {
    scene,
    renderer,
    camera,
    lights,
    houseRoot,
    debugRoot,
    setTheme(next) {
      const cols = THEME_COLORS[next];
      scene.background = new Color(cols.background);
      (scene.fog as Fog).color.setHex(cols.fog);
    },
    setCameraMode(mode) {
      camera.setMode(mode);
    },
    setAutoRotate(on) {
      autoRotate = on;
      resetIdle();
    },
    setSun(t) {
      lights.setSun(t);
    },
    setHingeAngle(id, t, inverted = false) {
      // First registration (fresh build) or inversion change: snap the
      // current value to the target so there's no "swing from 0" on
      // first reveal, and so flipping the swing direction doesn't
      // animate the leaf through the closed position.
      const prevInv = hingeInverted.get(id);
      const isFirst = !hingeCurrents.has(id);
      if (isFirst || prevInv !== inverted) {
        hingeCurrents.set(id, t);
        hingeInverted.set(id, inverted);
        hingeTargets.set(id, t);
        applyHingeCurrent(id);
        return;
      }
      // Normal case: just update the target — the tick will ease in.
      hingeTargets.set(id, t);
    },
    resetHingeAnimations() {
      hingeTargets.clear();
      hingeCurrents.clear();
      hingeInverted.clear();
    },
    enterEgoMode() {
      const spawn = computeEgoSpawn(houseRoot);
      ego.enter(spawn);
    },
    exitEgoMode() {
      ego.exit();
    },
    rebuildCollision() {
      const octree = buildCollisionOctree(houseRoot);
      ego.setOctree(octree);
    },
    setShowContext(on) {
      // Walk the live houseRoot — the names below are stable across rebuilds
      // because buildHouse always assigns them. We touch only `.visible` so
      // toggling back on doesn't require a full rebuild. Plinth + front
      // steps stay with the house because they're part of its volume; only
      // the lot plate, fence, mailbox, driveway and walkway are "site"
      // elements.
      //
      // The named site nodes live INSIDE the per-build root group
      // (`HouseRoot_Built`), which is the sole child of `houseRoot`. A
      // direct-children loop would only see that wrapper and silently
      // match nothing — that was the regression behind the broken toggle.
      // We traverse the whole subtree instead.
      houseRoot.traverse((child) => {
        if (
          child.name === 'BasePlate' ||
          child.name === 'Mailbox' ||
          child.name === 'Driveway' ||
          child.name === 'Walkway' ||
          child.name.startsWith('BasePlate_') ||
          child.name.startsWith('Fence')
        ) {
          child.visible = on;
        }
      });
    },
    setStoryPeel(count) {
      // Peel layers from the top down:
      //   count = 0   → nothing hidden.
      //   count = 1   → roof hidden (peek into the top story).
      //   count = k+1 → roof + k top-most stories hidden (k ≥ 1).
      //   count = N+2 → also hide the lot base plate (reveal basement).
      // Canonical state is never mutated (see Pitfall 7).
      const storyNodes: Object3D[] = [];
      const stairNodes: Object3D[] = [];
      const balconyNodes: Object3D[] = [];
      const terraceNodes: Object3D[] = [];
      const basePlateNodes: Object3D[] = [];
      const garageNodes: Object3D[] = [];
      // Things that ride along with the GROUND FLOOR (hide together
      // with story 0) — decks are patios attached to L0 and must not
      // float in mid-air once L0 is peeled.
      const groundFloorNodes: Object3D[] = [];
      // Site furniture / hardscape: everything that sits on the lot
      // base plate rather than on/in the house. These hide at max peel
      // so the basement well is genuinely the only thing visible when
      // the user drags peel to the bottom (user-reported: "really only
      // the basement should be visible").
      const siteFurnitureNodes: Object3D[] = [];
      houseRoot.traverse((n) => {
        if (n.name.startsWith('Story_')) storyNodes.push(n);
        else if (n.name.startsWith('Stairs_to_L')) stairNodes.push(n);
        else if (n.name.startsWith('Balcony_L')) balconyNodes.push(n);
        else if (n.name.startsWith('Terrace_L')) terraceNodes.push(n);
        else if (n.name === 'BasePlate' || n.name.startsWith('BasePlate_')) basePlateNodes.push(n);
        else if (n.name === 'Garage') garageNodes.push(n);
        else if (n.name === 'Deck') groundFloorNodes.push(n);
        else if (
          n.name === 'Walkway' ||
          n.name === 'Driveway' ||
          n.name === 'Plinth' ||
          n.name === 'FrontSteps' ||
          n.name === 'Mailbox' ||
          n.name.startsWith('Fence_')
        ) {
          siteFurnitureNodes.push(n);
        }
      });
      storyNodes.sort((a, b) => {
        const la = parseInt(a.name.slice('Story_'.length), 10);
        const lb = parseInt(b.name.slice('Story_'.length), 10);
        return la - lb;
      });
      const safe = Math.max(0, count);
      const roof = houseRoot.getObjectByName('Roof');
      if (roof) roof.visible = safe < 1;
      const storiesToHide = Math.max(0, safe - 1);
      const keep = Math.max(0, storyNodes.length - storiesToHide);
      storyNodes.forEach((n, i) => {
        n.visible = i < keep;
      });
      // A stair flight is anchored to the floor it RISES FROM — peeling
      // the upper destination away should still leave the flight standing
      // on its lower-story slab so you can read the cutaway. Hide it only
      // when the starting story itself is peeled.
      for (const stair of stairNodes) {
        const lvl = parseInt(stair.name.slice('Stairs_to_L'.length), 10);
        const startLevel = Number.isFinite(lvl) ? lvl - 1 : 0;
        stair.visible = startLevel < keep;
      }
      // Balconies disappear with the story they connect to. Without this
      // a balcony anchored to L1 floats in mid-air after L1 is peeled.
      for (const balc of balconyNodes) {
        const lvl = parseInt(balc.name.slice('Balcony_L'.length), 10);
        balc.visible = Number.isFinite(lvl) ? lvl < keep : true;
      }
      // Terraces likewise ride along with their host story: the three
      // interior-boundary walls and the railing are embedded in that
      // story's volume, so peeling the story should take the terrace
      // walls with it. We parse the level out of `Terrace_L<level>`
      // (the suffix after that is the side, which we ignore).
      for (const terr of terraceNodes) {
        const body = terr.name.slice('Terrace_L'.length);
        const lvl = parseInt(body, 10);
        terr.visible = Number.isFinite(lvl) ? lvl < keep : true;
      }
      // The garage (and decks) are siblings of the body that attach
      // conceptually to L0; they disappear in lockstep with story 0.
      // Without this they float in mid-air after the ground floor is
      // peeled.
      const groundKept = keep > 0;
      for (const g of garageNodes) g.visible = groundKept;
      for (const g of groundFloorNodes) g.visible = groundKept;
      // Final peel step (count > storyNodes.length + 1): hide the lot
      // base plate so the basement well is no longer occluded by it.
      // At that same level the surrounding site furniture (walkway,
      // driveway, plinth/front steps, fence, mailbox) also goes dark
      // so the basement is the only structure left visible — the
      // whole lot treatment reads as "looking at the crawl space
      // through the lot floor".
      const hideBasePlate = safe > storyNodes.length + 1;
      for (const bp of basePlateNodes) bp.visible = !hideBasePlate;
      for (const s of siteFurnitureNodes) s.visible = !hideBasePlate;
    },
    requestRedraw() {
      // Single-frame nudge; the rAF loop already runs, so this is just semantic.
      resetIdle();
    },
    dispose() {
      running = false;
      ro.disconnect();
      renderer.domElement.removeEventListener('pointerdown', resetIdle);
      renderer.domElement.removeEventListener('wheel', resetIdle);
      window.removeEventListener('keydown', resetIdle);
      ego.dispose();
      disposeSubtree(scene);
      lights.dispose();
      camera.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    },
  };

  // Bridge the controller's "browser released my pointer lock" signal
  // back to the React store, so pressing ESC exits ego mode globally
  // (not just at the controller layer). The viewport doesn't import
  // zustand itself — we let the controller broadcast via a callback.
  ego.onExitRequested = () => {
    viewport.exitEgoMode();
    opts.onEgoExit?.();
  };

  return viewport;
}

/**
 * Pick an ego-mode spawn point 1.5 m INSIDE the front gate, on the lot
 * side, facing the centre of the house. Falls back to a safe lot-centre
 * spawn if the expected nodes are absent (e.g. fence disabled, unusual
 * footprint). The gate leaf and the walkway both sit on the world +Z
 * side of the gate pivot (see buildFence.ts), so "into the lot" means
 * +Z from the gate's world position.
 */
function computeEgoSpawn(houseRoot: Group): { x: number; y: number; z: number; yaw: number } {
  const gate = houseRoot.getObjectByName('Hinge_gate-front');
  let gateWorld: Vector3 | null = null;
  if (gate) {
    gateWorld = new Vector3();
    gate.getWorldPosition(gateWorld);
  }
  // House target point: the built-house group is centred on 0,0 but its
  // children carry real footprint offsets. Average all mesh world
  // positions with Y below roof height would be overkill — we use the
  // bounding-box centre of the named HouseRoot_Built wrapper instead.
  const built = houseRoot.getObjectByName('HouseRoot_Built');
  const houseCenter = new Vector3();
  if (built) {
    built.getWorldPosition(houseCenter);
  }
  if (gateWorld) {
    const dx = houseCenter.x - gateWorld.x;
    const dz = houseCenter.z - gateWorld.z;
    const len = Math.hypot(dx, dz) || 1;
    const nx = dx / len;
    const nz = dz / len;
    const spawnX = gateWorld.x + nx * 1.5;
    const spawnZ = gateWorld.z + nz * 1.5;
    // Three.js camera with YXZ Euler: yaw=0 faces -Z. To face (nx, nz)
    // we need yaw = atan2(-nx, -nz).
    const yaw = Math.atan2(-nx, -nz);
    return { x: spawnX, y: 0, z: spawnZ, yaw };
  }
  // Fallback: drop outside the south lot edge looking +Z.
  return { x: 0, y: 0, z: 6, yaw: Math.PI };
}
